import { NoContentBox, sidebarCollapsedObserver } from '@enkla-gui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const RECENTS_KEY = 'search_recents'
const RECENTS_MAX = 6

function loadRecents() {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY)) || []
  } catch {
    return []
  }
}

function saveRecent(hit) {
  const recents = loadRecents().filter(r => r._id !== hit._id)
  recents.unshift(hit)
  localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, RECENTS_MAX)))
}

function clearRecentsStorage() {
  localStorage.removeItem(RECENTS_KEY)
}

function groupSearchResults(hits) {
  if (!hits?.length) return []

  const idMap = {}
  const leases = []
  const others = []

  for (const hit of hits) {
    if (hit._index === 'leases') {
      leases.push(hit)
    } else {
      idMap[hit._id] = { hit, children: [] }
      others.push(hit)
    }
  }

  const grouped = new Set()

  for (const lease of leases) {
    const relations = lease._source?.render?.relations
    if (!relations) continue

    const parentId = relations.entity_id && idMap[relations.entity_id]
      ? relations.entity_id
      : relations.tenant_ids?.find(tid => idMap[tid]) || null

    if (parentId) {
      idMap[parentId].children.push(lease)
      grouped.add(lease._id)
    }
  }

  const result = []
  for (const other of others) {
    const entry = idMap[other._id]
    result.push({ hit: entry.hit, children: entry.children })
  }
  for (const lease of leases) {
    if (!grouped.has(lease._id)) {
      result.push({ hit: lease, children: [] })
    }
  }

  return result
}

function getContextActions(hits) {
  if (!hits?.length) return []
  const actions = []
  const seen = new Set()

  for (const hit of hits.slice(0, 5)) {
    const render = hit._source?.render
    if (!render) continue

    if (hit._index === 'companies' && !seen.has('entity-company')) {
      actions.push({ label: `Skapa objekt: ${render.title}`, link: `#!/entity/create/company/${hit._id}`, actionIcon: 'fa-plus' })
      seen.add('entity-company')
    }
    if (hit._index === 'tenants' && !seen.has('ticket-tenant')) {
      actions.push({ label: `Skapa ärende: ${render.title}`, link: `#!/ticket/create/tenant/${hit._id}`, actionIcon: 'fa-plus' })
      seen.add('ticket-tenant')
    }
    if (hit._index === 'tenants' && !seen.has('lease-tenant')) {
      actions.push({ label: `Skapa uthyrning: ${render.title}`, link: `#!/lease/create/tenant/${hit._id}`, actionIcon: 'fa-handshake' })
      seen.add('lease-tenant')
    }
    if (hit._index === 'entities' && !seen.has('ticket-entity')) {
      actions.push({ label: `Skapa ärende: ${render.title}`, link: `#!/ticket/create/entity/${hit._id}`, actionIcon: 'fa-plus' })
      seen.add('ticket-entity')
    }
    if (hit._index === 'entities' && !seen.has('lease-entity')) {
      actions.push({ label: `Skapa uthyrning: ${render.title}`, link: `#!/lease/create/entity/${hit._id}`, actionIcon: 'fa-handshake' })
      seen.add('lease-entity')
    }
    if (actions.length >= 4) break
  }
  return actions
}

const STATIC_ACTIONS = [
  { label: 'Skapa objekt', link: '#!/entity/create', actionIcon: 'fa-layer-group' },
  { label: 'Skapa hyresgäst', link: '#!/tenant/create', actionIcon: 'fa-user-plus' },
  { label: 'Skapa uthyrning', link: '#!/lease/create', actionIcon: 'fa-handshake' },
]

function buildRenderItems({ contextActions, groups, recents, showEmpty }) {
  const items = []
  let navIndex = 0

  if (showEmpty) {
    items.push({ kind: 'header', label: 'Snabbkommandon', icon: 'fa-bolt' })
    for (const action of STATIC_ACTIONS) {
      items.push({ kind: 'action', ...action, navIndex: navIndex++ })
    }
    if (recents.length > 0) {
      items.push({ kind: 'header', label: 'Senaste', icon: 'fa-clock', clearable: true })
      for (const hit of recents) {
        items.push({ kind: 'result', hit, navIndex: navIndex++ })
      }
    }
  } else {
    for (const action of contextActions) {
      items.push({ kind: 'action', ...action, navIndex: navIndex++ })
    }
    if (contextActions.length > 0) {
      items.push({ kind: 'divider' })
    }
    for (let gi = 0; gi < groups.length; gi++) {
      const group = groups[gi]
      if (gi > 0) items.push({ kind: 'group-sep' })
      items.push({ kind: 'result', hit: group.hit, navIndex: navIndex++ })
      for (let ci = 0; ci < group.children.length; ci++) {
        items.push({
          kind: 'result',
          hit: group.children[ci],
          navIndex: navIndex++,
          isChild: true,
          isLast: ci === group.children.length - 1
        })
      }
    }
  }

  return { items, navCount: navIndex }
}

const SearchResult = React.forwardRef(({ hit, isChild, isLast, onSelect, active }, ref) => {
  const render = hit._source?.render
  if (!render) return null

  return (
    <a
      ref={ref}
      href={render.link}
      class={`search-result ${isChild ? 'search-group-child' : ''} ${isChild && isLast ? 'last' : ''} ${active ? 'search-item-active' : ''}`}
      onClick={() => onSelect(hit)}>
      <span class='search-result-title'>
        <span class='search-result-name'>{render.title}</span>
        {render.subtitle && <span class='search-result-subtitle'>{render.subtitle}</span>}
      </span>
      <span class='search-result-meta'>
        <span class={`search-badge search-badge-${hit._index}`}>
          <i class={`fas fa-fw ${render.icon}`} /> <span>{render.type}</span>
        </span>
        <i class='fas fa-chevron-right search-result-arrow' />
      </span>
    </a>
  )
})

const ActionRow = React.forwardRef(({ label, link, actionIcon, active, onClick }, ref) => (
  <a
    ref={ref}
    href={link}
    class={`search-action ${active ? 'search-item-active' : ''}`}
    onClick={onClick}>
    <i class={`fas fa-fw ${actionIcon} search-action-icon`} />
    <span class='search-action-label'>{label}</span>
    <i class='fas fa-chevron-right search-result-arrow' />
  </a>
))

export default function Navbar({ search = false, children }) {
  const [collapsed, setCollapsed] = useState(sidebarCollapsedObserver.value)
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const [recents, setRecents] = useState([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef(null)
  const itemRefs = useRef({})

  useEffect(() => {
    sidebarCollapsedObserver.subscribe(x => setCollapsed(x))
  }, [collapsed])

  useEffect(() => {
    if (!search) return
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [search])

  useEffect(() => {
    setResults([])
    if (query.trim().length === 0) {
      setResults([])
      setSearching(false)
      return
    }
    if (!query.length !== 0 && !searching) setSearching(true)
    const timeoutId = setTimeout(() => dispatchSearch(query.trim()), 500)
    return () => clearTimeout(timeoutId)
  }, [query])

  useEffect(() => {
    setActiveIndex(-1)
  }, [results, recents, focused])

  const dispatchSearch = q => {
    search(q)
      .then(response => {
        setSearching(false)
        setResults(response.hits)
      })
      .catch(() => {
        setResults([])
        setSearching(false)
      })
  }

  const groups = useMemo(() => groupSearchResults(results?.hits), [results])
  const contextActions = useMemo(() => getContextActions(results?.hits), [results])
  const showEmpty = focused && query.trim().length === 0 && !searching && results.length === 0

  const renderData = useMemo(() => {
    if (showEmpty) {
      return buildRenderItems({ contextActions: [], groups: [], recents, showEmpty: true })
    }
    if (results?.total?.value) {
      return buildRenderItems({ contextActions, groups, recents: [], showEmpty: false })
    }
    return { items: [], navCount: 0 }
  }, [showEmpty, recents, results, contextActions, groups])

  const toggle = () => sidebarCollapsedObserver.next(!sidebarCollapsedObserver.value)

  const close = useCallback(() => {
    setResults([])
    setFocused(false)
    setQuery('')
    setActiveIndex(-1)
    if (inputRef.current) inputRef.current.value = ''
  }, [])

  const handleSelect = useCallback(hit => {
    saveRecent(hit)
    close()
  }, [close])

  const handleFocus = () => {
    setFocused(true)
    setRecents(loadRecents())
  }

  const handleClearRecents = e => {
    e.stopPropagation()
    clearRecentsStorage()
    setRecents([])
  }

  const handleKeyDown = e => {
    const { navCount, items } = renderData
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      inputRef.current?.blur()
      return
    }
    if (!navCount) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex(prev => {
          const next = Math.min(prev + 1, navCount - 1)
          setTimeout(() => itemRefs.current[next]?.scrollIntoView({ block: 'nearest' }), 0)
          return next
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex(prev => {
          const next = Math.max(prev - 1, 0)
          setTimeout(() => itemRefs.current[next]?.scrollIntoView({ block: 'nearest' }), 0)
          return next
        })
        break
      case 'Enter':
        if (activeIndex >= 0) {
          e.preventDefault()
          const item = items.find(i => i.navIndex === activeIndex)
          if (item) {
            if (item.hit) saveRecent(item.hit)
            const link = item.link || item.hit?._source?.render?.link
            if (link) {
              window.location.hash = link.replace('#', '')
              close()
            }
          }
        }
        break
    }
  }

  const hasContent = renderData.items.length > 0
  const hasResults = results.length !== 0 && !!results?.total?.value
  const noResults = results.length !== 0 && results?.total?.value === 0

  const renderItem = (item, i) => {
    if (item.kind === 'header') {
      return (
        <div class='search-section-header' key={`h-${i}`}>
          <span><i class={`fas ${item.icon}`} /> {item.label}</span>
          {item.clearable && <button onClick={handleClearRecents}>Rensa</button>}
        </div>
      )
    }
    if (item.kind === 'action') {
      return (
        <ActionRow
          key={`a-${i}`}
          ref={el => { itemRefs.current[item.navIndex] = el }}
          label={item.label}
          link={item.link}
          actionIcon={item.actionIcon}
          active={item.navIndex === activeIndex}
          onClick={close}
        />
      )
    }
    if (item.kind === 'result') {
      return (
        <SearchResult
          key={`r-${i}`}
          ref={el => { itemRefs.current[item.navIndex] = el }}
          hit={item.hit}
          isChild={item.isChild}
          isLast={item.isLast}
          active={item.navIndex === activeIndex}
          onSelect={handleSelect}
        />
      )
    }
    if (item.kind === 'divider') {
      return <div key={`d-${i}`} class='search-section-divider' />
    }
    if (item.kind === 'group-sep') {
      return <div key={`gs-${i}`} class='search-group-border' />
    }
    return null
  }

  return (
    <nav class='navbar sticky-top navbar-expand-lg mb-3 component_Navbar'>
      <div class='navbar-brand' onClick={() => toggle()}>
        <i class={`fa fa-bars ${collapsed ? 'fa-rotate-90' : ''}`} />
      </div>
      {search && (
        <div class='navbar-search'>
          <input
            ref={inputRef}
            type='text'
            class='form-control'
            placeholder='Sök...'
            onChange={e => setQuery(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onClick={e => {
              if (query.length === 0) return
              if (!searching) setSearching(true)
              dispatchSearch(e.target.value)
            }}
          />
          <span class='search-shortcut-hint'>⌘K</span>
          {(showEmpty && hasContent) && (
            <div class='card navbar-search-results'>
              <div class='search-clear-results' onClick={close} />
              <div class='card-body'>
                <div class='search-results-list'>
                  {renderData.items.map(renderItem)}
                </div>
              </div>
            </div>
          )}
          {hasResults && (
            <div class='card navbar-search-results'>
              <div class='search-clear-results' onClick={close} />
              <div class='card-body'>
                <div class='search-results-list'>
                  {renderData.items.map(renderItem)}
                </div>
              </div>
            </div>
          )}
          {noResults && !searching && (
            <div class='card navbar-search-results'>
              <div class='search-clear-results' onClick={close} />
              <div class='card-body'>
                <NoContentBox text='Inga resultat' icon='fa-info-circle' />
              </div>
            </div>
          )}
          {searching && (
            <div class='card navbar-search-results' onClick={close}>
              <div class='search-clear-results' />
              <div class='card-body no-padding'>
                <NoContentBox text='Söker...' icon='fa-circle-notch fa-spin' />
              </div>
            </div>
          )}
        </div>
      )}
      <div class='navbar-nav'>{children}</div>
    </nav>
  )
}
